import { builderSampleSchedule } from "../metadata-builder/sample-schedule";
import { builderSessionId, useMetadataBuilderStore, type BuilderStep } from "../../stores/metadata-builder-store";
import { useMetadataHistoryStore } from "../../stores/metadata-history-store";
import { useScheduleStore } from "../../stores/schedule-store";
import { useMemo } from "react";
import { builderDayLayoutMode, dayLayoutTargets } from "../metadata-builder/day-layouts";
import { metadataTargets } from "../metadata-history/metadata-targets";
import { builderTextVisible } from "../metadata-builder/preview-visibility";
import { previewModesFor, previewSlotCounts, visiblePreviewDays, type PreviewDayModes } from "../metadata-builder/preview-modes";
import type { MetadataDay } from "../metadata-history/metadata-targets";

function dayDatesVisible(step: BuilderStep) {
  return step === "oneSlot" || step === "twoSlot" || step === "review";
}

function editsDayAnchors(step: BuilderStep) {
  return step === "offline" || step === "oneSlot" || step === "twoSlot";
}

type BuilderPreview = { step: BuilderStep; previewDay: MetadataDay | null; previewModes: PreviewDayModes };

// Edit steps focus one layout mode. Other steps render the per-day preview modes.
function builderPreviewModes(builder: BuilderPreview) {
  const mode = builderDayLayoutMode(builder.step);
  if (!mode) return builder.previewModes;
  return previewModesFor(mode);
}

function builderPreviewDays(builder: BuilderPreview) {
  const mode = builderDayLayoutMode(builder.step);
  if (!mode) return visiblePreviewDays(builder.previewModes);
  if (!builder.previewDay) return null;
  return [builder.previewDay];
}

export function useCanvasDayAnchorsEnabled() {
  const active = useMetadataBuilderStore(state => state.active);
  const step = useMetadataBuilderStore(state => state.step);
  return !active || editsDayAnchors(step);
}

export function useCanvasDayDatesVisible() {
  return useMetadataBuilderStore(state => !state.active || dayDatesVisible(state.step));
}

export function getCanvasState() {
  const schedule = useScheduleStore.getState();
  const builder = useMetadataBuilderStore.getState();
  if (!builder.active) return { ...schedule, dayDatesVisible: true, previewDays: null, builderDayLayout: undefined, builderStep: undefined };
  const session = useMetadataHistoryStore.getState().sessions[builderSessionId];
  const mode = builderDayLayoutMode(builder.step);
  const reviewing = builder.step === "review";
  return {
    ...schedule, ...builderSampleSchedule(previewSlotCounts(builderPreviewModes(builder)), builder),
    profile: { artist_handle: builder.sampleArtistHandle },
    canvasMetadata: session?.projection ?? null, developerMode: !reviewing,
    anchorsVisible: schedule.anchorsVisible && editsDayAnchors(builder.step),
    dayDatesVisible: dayDatesVisible(builder.step),
    previewDays: builderPreviewDays(builder),
    builderDayLayout: mode ? session?.dayLayouts?.[mode] : undefined,
    builderStep: builder.step,
  };
}

export function getCanvasMetadataTargets() {
  const state = getCanvasState();
  const metadata = state.canvasMetadata ?? state.preset.metadata;
  const targets = metadataTargets(metadata);
  return dayLayoutTargets(targets, state.builderDayLayout, state.previewDays)
    .filter(target => {
      if (state.builderStep && !builderTextVisible(state.builderStep, target.path, target.bind)) return false;
      if (target.bind !== "day.date" || !target.day) return true;
      return state.dayDatesVisible && metadata.formats[metadata.days[target.day].date.format] !== "";
    });
}

export function useCanvasMetadataTargets() {
  const metadata = useCanvasMetadata();
  const active = useMetadataBuilderStore(state => state.active);
  const step = useMetadataBuilderStore(state => state.step);
  const previewDay = useMetadataBuilderStore(state => state.previewDay);
  const previewModes = useMetadataBuilderStore(state => state.previewModes);
  const layouts = useMetadataHistoryStore(state => state.sessions[builderSessionId]?.dayLayouts);
  return useMemo(() => {
    const mode = active ? builderDayLayoutMode(step) : undefined;
    const previewDays = active ? builderPreviewDays({ step, previewDay, previewModes }) : null;
    const targets = dayLayoutTargets(metadataTargets(metadata), mode ? layouts?.[mode] : undefined, previewDays);
    return targets.filter(target => {
      if (active && !builderTextVisible(step, target.path, target.bind)) return false;
      if (target.bind !== "day.date" || !target.day) return true;
      if (active && !dayDatesVisible(step)) return false;
      return metadata.formats[metadata.days[target.day].date.format] !== "";
    });
  }, [metadata, active, step, previewDay, previewModes, layouts]);
}

export function useCanvasMetadata() {
  const active = useMetadataBuilderStore(state => state.active);
  const builderMetadata = useMetadataHistoryStore(state => state.sessions[builderSessionId]?.projection);
  const scheduleMetadata = useScheduleStore(state => state.canvasMetadata ?? state.preset.metadata);
  return active && builderMetadata ? builderMetadata : scheduleMetadata;
}

export function getCanvasMetadataSessionId() {
  if (useMetadataBuilderStore.getState().active) return builderSessionId;
  return useMetadataHistoryStore.getState().activeId;
}

export function useCanvasEditingEnabled() {
  const active = useMetadataBuilderStore(state => state.active);
  const step = useMetadataBuilderStore(state => state.step);
  const developerMode = useScheduleStore(state => state.developerMode);
  return active ? step !== "review" : developerMode;
}

export function useCanvasMetadataSessionId() {
  const active = useMetadataBuilderStore(state => state.active);
  const presetId = useMetadataHistoryStore(state => state.activeId);
  return active ? builderSessionId : presetId;
}
